import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../utils/AuthContext";
import HeadersTab from "../components/tabs/HeadersTab";
import InfoTab from "../components/tabs/InfoTab";
import MediaTab from "../components/tabs/MediaTab";
import VaultTab from "../components/tabs/VaultTab";
import DRDocsTab from "../components/tabs/DRDocsTab";
import DRDocsTab2 from "../components/tabs/DRDocsTab2";
import DataroomTab from "../components/tabs/DataroomTab";
import apiService from "../services/apiService";
import { API_CONFIG } from "../config/api";
import { FiExternalLink, FiCopy } from "react-icons/fi";
import DRDocsTab3 from "../components/tabs/DRDocsTab3";

const PropertyDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { propertyDetailsTabs, hasTabAccess } = useAuth();
  const [activeTab, setActiveTab] = useState("summary");
  const [propertyData, setPropertyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Tab configuration mapping
  const tabConfig = [
    { id: "summary", label: "Summary", component: null },
    { id: "headers", label: "Headers", component: HeadersTab },
    { id: "info", label: "Information", component: InfoTab },
    { id: "media", label: "Media", component: MediaTab },
    { id: "vault", label: "Vault", component: VaultTab },
    { id: "drdocs", label: "Discovery Data Room", component: DRDocsTab },
    {
      id: "duediligence",
      label: "Due Diligence Data Room",
      component: DRDocsTab2,
    },
    {
      id: "externalduediligence",
      label: "Engagement Room",
      component: DRDocsTab3,
    },
    { id: "dataroom", label: "Manage Datarooms", component: DataroomTab },
  ];

  // Filter tabs based on user permissions
  const allowedTabs = tabConfig.filter((tab) => hasTabAccess(tab.id));

  // Set default active tab to first allowed tab
  useEffect(() => {
    if (
      allowedTabs.length > 0 &&
      !allowedTabs.find((tab) => tab.id === activeTab)
    ) {
      setActiveTab(allowedTabs[0].id);
    }
  }, [propertyDetailsTabs]);

  useEffect(() => {
    if (activeTab === "summary" || activeTab === "headers") {
      fetchPropertySummary();
    }
  }, [id, activeTab]);

  const fetchPropertySummary = async (options = {}) => {
    const { silent = false } = options;
    try {
      if (!silent) setLoading(true);

      const data = await apiService.post(`/properties/${id}/admin`, {
        loggedInUserId: 4,
      });

      setPropertyData(data);
      setError(null);
    } catch (err) {
      console.error("Error fetching property details:", err);
      if (!silent) setError("Failed to load property details");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const renderSummaryTab = () => {
    if (!propertyData) return null;

    return (
      <div className="summary-content">
        <div className="summary-grid">
          <div className="summary-section">
            <h3>Property Information</h3>
            <div className="info-row">
              <span className="label">Reference:</span>
              <span className="value">{propertyData.propertyReference}</span>
            </div>
            <div className="info-row">
              <span className="label">Property name:</span>
              <span className="value">{propertyData.title}</span>
            </div>
            <div className="info-row">
              <span className="label">Category:</span>
              <span className="value">{propertyData.category}</span>
            </div>
            <div className="info-row">
              <span className="label">Status:</span>
              <span className="value">{propertyData.propertyStatus}</span>
            </div>
            <div className="info-row">
              <span className="label">Seller:</span>
              <span className="value">
                {propertyData.sellerCompany?.companyName}
              </span>
            </div>
            <div className="info-row">
              <span className="label">Portfolio:</span>
              <span className="value">{propertyData.portfolioTitle || '-'}</span>
            </div>
            <div className="info-row">
              <span className="label">Description:</span>
              <span className="value">{propertyData.shortDescription}</span>
            </div>
          </div>

          <div className="summary-section">
            <h3>Location</h3>
            <div className="info-row">
              <span className="label">Address:</span>
              <span className="value">{propertyData.addressLine1}</span>
            </div>
            {propertyData.addressLine2 && (
              <div className="info-row">
                <span className="label">Address 2:</span>
                <span className="value">{propertyData.addressLine2}</span>
              </div>
            )}
            <div className="info-row">
              <span className="label">Suburb:</span>
              <span className="value">{propertyData.suburb}</span>
            </div>
            <div className="info-row">
              <span className="label">Province:</span>
              <span className="value">{propertyData.province}</span>
            </div>
            <div className="info-row">
              <span className="label">Country:</span>
              <span className="value">{propertyData.country}</span>
            </div>
          </div>

          <div className="summary-section">
            <h3>Agent Details</h3>
            {propertyData.agent && (
              <>
                <div className="info-row">
                  <span className="label">Name:</span>
                  <span className="value">
                    {propertyData.agent.firstName} {propertyData.agent.lastName}
                  </span>
                </div>
                <div className="info-row">
                  <span className="label">Email:</span>
                  <span className="value">
                    {propertyData.agent.emailAddress}
                  </span>
                </div>
                <div className="info-row">
                  <span className="label">Contact number:</span>
                  <span className="value">
                    {propertyData.agent.cellNumber}
                  </span>
                </div>                
                <div className="info-row">
                  <span className="label">Agency:</span>
                  <span className="value">
                    {propertyData.agent.agency.name}
                  </span>
                </div>
              </>
            )}
          </div>

          <div className="summary-section">
            <h3>Property Flags</h3>
            <div className="info-row">
              <span className="label">Carousel Featured:</span>
              <span
                className={`value ${propertyData.isFeatured ? "yes" : "no"}`}
              >
                {propertyData.isFeatured ? "Yes" : "No"}
              </span>
            </div>
            <div className="info-row">
              <span className="label">Public Listing:</span>
              <span
                className={`value ${propertyData.isListable ? "yes" : "no"}`}
              >
                {propertyData.isListable ? "Yes" : "No"}
              </span>
            </div>
            <div className="info-row">
              <span className="label">Discrete List:</span>
              <span
                className={`value ${propertyData.isDiscrete ? "yes" : "no"}`}
              >
                {propertyData.isDiscrete ? "Yes" : "No"}
              </span>
            </div>
            <div className="info-row">
              <span className="label">Published:</span>
              <span className={`value ${propertyData.isActive ? "yes" : "no"}`}>
                {propertyData.isActive ? "Yes" : "No"}
              </span>
            </div>
            <div className="info-row">
              <span className="label">Archived:</span>
              <span
                className={`value ${propertyData.isArchived ? "yes" : "no"}`}
              >
                {propertyData.isArchived ? "Yes" : "No"}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderTabContent = () => {
    const currentTab = allowedTabs.find((tab) => tab.id === activeTab);

    if (!currentTab) {
      return allowedTabs.length > 0 ? renderSummaryTab() : null;
    }

    switch (currentTab.id) {
      case "summary":
        return renderSummaryTab();
      case "headers":
        return (
          <currentTab.component
            propertyData={propertyData}
            onTabChange={setActiveTab}
          />
        );
      case "vault":
        return (
          <currentTab.component
            propertyData={propertyData}
            onPropertyDataRefresh={() => fetchPropertySummary({ silent: true })}
          />
        );
      case "info":
        return <currentTab.component propertyData={propertyData} />;
      case "media":
      case "drdocs":
      case "duediligence":
      case "externalduediligence":
      case "dataroom":
        return <currentTab.component />;
      default:
        return renderSummaryTab();
    }
  };

  const handleCopyLink = () => {
    const url = `${API_CONFIG.PUBLIC_SITE_BASE_URL}/property-details/${id}?${propertyData.title?.replace(/\s+/g, '_') || ''}`;

    navigator.clipboard
      .writeText(url)
      .then(() => {
        alert("Link copied to clipboard!");
      })
      .catch((err) => {
        console.error("Failed to copy link:", err);
        alert("Failed to copy link");
      });
  };

  if (loading) {
    return (
      <div className="property-details">
        <div className="loading">Loading property details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="property-details">
        <div className="error">
          <p>{error}</p>
          <button onClick={() => navigate("/properties")}>
            Back to Properties
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="property-details">
      <div className="page-header">
        <h1 style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          Property Details{" "}
          {propertyData?.title ? `- ${propertyData.title}` : ``}
          <button
            onClick={() =>
              window.open(
                `${API_CONFIG.PUBLIC_SITE_BASE_URL}/property-details/${id}?${propertyData.title?.replace(/\s+/g, '_') || ''}`,
                `property-public-${id}`
              )
            }
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "#0066cc",
              display: "flex",
              alignItems: "center",
              padding: "5px",
              fontSize: "20px",
            }}
            title="View property on public site"
          >
            <FiExternalLink />
          </button>
          <button
            onClick={handleCopyLink}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "#0066cc",
              display: "flex",
              alignItems: "center",
              padding: "5px",
              fontSize: "20px",
            }}
            title="Copy property link for email"
          >
            <FiCopy />
          </button>
        </h1>
      </div>

      <div className="tabs-container">
        <div className="tabs-header">
          {allowedTabs.map((tab) => (
            <button
              key={tab.id}
              className={`tab-button ${activeTab === tab.id ? "active" : ""}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="tab-content-container">{renderTabContent()}</div>
      </div>
    </div>
  );
};

export default PropertyDetails;
